// Foto (DESIGN §5.14.1) e FotoSobre (§5.7): fotos que carregam sem parecer quebradas
// (docs/prototipo/comunidade-fotos-marcacao.html §1 a §3 e §10, aprovada pelo founder).
//
// Foto: a moldura `recuo` existe ANTES da foto, na proporção final (o layout não pula). 0–300 ms liso; depois o
// brilho do esqueleto; a foto entra com fade de 200 ms (`--gs-dur-estado`, `ease-gs`), ou direto se já estava no
// cache. Falhou (404, sem rede, 20 s no app): o bloco inteiro vira botão "Não carregou / Toque para tentar de novo",
// que volta ao brilho na hora. Movimento reduzido: sem brilho e fade de 100 ms (regra global dos tokens).
// O `<img>` NUNCA é renderizado sem `src`.
//
// FotoSobre: foto por cima de algo que já está na tela (iniciais do Avatar, maçã do parceiro), sem esqueleto e sem
// "tentar de novo": se falhar, some e fica o que estava embaixo.
import { useEffect, useLayoutEffect, useRef, useState, type ImgHTMLAttributes, type RefObject } from "react";
import { ImageOff } from "lucide-react";
import { useArquivo } from "@plataforma/midia";
import type { EstadoArquivo } from "../../plataforma/tipos";
import { cn } from "../lib/cn";
import { useAtraso } from "../lib/hooks";

export type ProporcaoFoto = "4/5" | "1/1" | "16/9";
export type EstadoFoto = "aguardando" | "carregando" | "pronta" | "erro";

const ASPECTO: Record<ProporcaoFoto, string> = {
  "4/5": "aspect-[4/5]",
  "1/1": "aspect-square",
  "16/9": "aspect-[16/9]",
};

/** Estado visível da Foto a partir do arquivo (plataforma) e do `<img>` (decodificou ou falhou). */
export function estadoDaFoto(p: {
  arquivo: EstadoArquivo;
  decodificada: boolean;
  falhouImg: boolean;
  brilho: boolean;
}): EstadoFoto {
  if (p.arquivo === "erro" || p.falhouImg) return "erro";
  if (p.arquivo === "pronta" && p.decodificada) return "pronta";
  return p.brilho ? "carregando" : "aguardando";
}

/** Situação do `<img>` desta tentativa (load/error), zerada a cada "tentar de novo". */
function useImagem(tentativa: number) {
  const [img, setImg] = useState<{ tentativa: number; ok: boolean } | null>(null);
  const atual = img?.tentativa === tentativa ? img : null;
  return {
    decodificada: atual?.ok === true,
    falhou: atual?.ok === false,
    aoCarregar: () => setImg({ tentativa, ok: true }),
    aoFalhar: () => setImg({ tentativa, ok: false }),
  };
}

/**
 * Web: imagem que já estava no cache do navegador chega `complete` na montagem; aparece direto, sem fade.
 * (No app, `emCache` vem da plataforma.)
 */
function useJaCompleta(ref: RefObject<HTMLImageElement | null>, aoCompleta: () => void) {
  const [completa, setCompleta] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el?.complete && el.naturalWidth > 0) {
      setCompleta(true);
      aoCompleta();
    }
    // só na montagem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return completa;
}

export interface PropsFoto {
  src: string;
  /** O alt de sempre ("Foto 1 do post de Rafael"); também compõe o rótulo do erro. */
  alt: string;
  proporcao: ProporcaoFoto;
  className?: string;
  loading?: ImgHTMLAttributes<HTMLImageElement>["loading"];
  /** A grade do post leva `aria-busy` enquanto alguma célula carrega. */
  aoMudarEstado?: (estado: EstadoFoto) => void;
}

export function Foto({ src, alt, proporcao, className, loading = "lazy", aoMudarEstado }: PropsFoto) {
  const arquivo = useArquivo(src);
  const img = useImagem(arquivo.tentativa);
  const ref = useRef<HTMLImageElement>(null);
  const [semFade] = useState(arquivo.emCache);
  const jaCompleta = useJaCompleta(ref, img.aoCarregar);
  // Depois de tocar em "tentar de novo", o brilho volta na hora (sem os 300 ms lisos).
  const [brilhoImediato, setBrilhoImediato] = useState(false);
  const esperando = arquivo.estado !== "erro" && !img.falhou && !img.decodificada;
  const brilhoAtrasado = useAtraso(esperando, 300);
  const estado = estadoDaFoto({
    arquivo: arquivo.estado,
    decodificada: img.decodificada,
    falhouImg: img.falhou,
    brilho: brilhoAtrasado || brilhoImediato,
  });

  const avisar = useRef(aoMudarEstado);
  avisar.current = aoMudarEstado;
  useEffect(() => {
    avisar.current?.(estado);
  }, [estado]);

  const moldura = cn(
    "relative block w-full overflow-hidden bg-recuo",
    ASPECTO[proporcao],
    estado === "carregando" && "esqueleto",
    className,
  );

  if (estado === "erro") {
    return (
      <button
        type="button"
        className={cn(moldura, "text-texto-3")}
        aria-label={`${alt} não carregou. Tentar de novo`}
        onClick={() => {
          setBrilhoImediato(true);
          arquivo.tentarDeNovo();
        }}
      >
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-center">
          <ImageOff size={24} aria-hidden />
          <b className="text-apoio font-medium text-texto-2">Não carregou</b>
          <span className="text-rotulo">Toque para tentar de novo</span>
        </span>
      </button>
    );
  }

  return (
    <span className={moldura}>
      {arquivo.url && (
        <img
          key={arquivo.tentativa}
          ref={ref}
          src={arquivo.url}
          alt={alt}
          loading={loading}
          decoding="async"
          onLoad={img.aoCarregar}
          onError={img.aoFalhar}
          className={cn(
            "absolute inset-0 size-full object-cover",
            !(semFade || jaCompleta) && "transition-opacity duration-(--gs-dur-estado) ease-gs",
            estado === "pronta" ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </span>
  );
}

/**
 * Foto por cima do que já está desenhado (iniciais do Avatar, maçã do parceiro): `absolute inset-0`, fade de 200 ms
 * quando chega, some se falhar. Decorativa (`alt=""`): o nome acessível fica no contêiner.
 */
export function FotoSobre({ src, className }: { src: string; className?: string }) {
  const arquivo = useArquivo(src);
  const img = useImagem(arquivo.tentativa);
  const ref = useRef<HTMLImageElement>(null);
  const [semFade] = useState(arquivo.emCache);
  const jaCompleta = useJaCompleta(ref, img.aoCarregar);
  if (!arquivo.url || arquivo.estado === "erro" || img.falhou) return null;
  return (
    <img
      ref={ref}
      src={arquivo.url}
      alt=""
      decoding="async"
      onLoad={img.aoCarregar}
      onError={img.aoFalhar}
      className={cn(
        "absolute inset-0 size-full object-cover",
        !(semFade || jaCompleta) && "transition-opacity duration-(--gs-dur-estado) ease-gs",
        img.decodificada ? "opacity-100" : "opacity-0",
        className,
      )}
    />
  );
}
